import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchCampaigns,
  fetchDisclosures,
  fetchTags,
  fetchTenants,
  fetchTriggers,
  fetchVariables,
} from '../api/templatesMetaApi';
import { fetchTenantSettings } from '../api/tenantSettingsApi';
import {
  templatesCampaignsFailed,
  templatesCampaignsReceived,
  templatesCampaignsRequested,
  templatesTagsFailed,
  templatesTagsReceived,
  templatesTagsRequested,
  templatesTriggersFailed,
  templatesTriggersReceived,
  templatesTriggersRequested,
  templatesTriggersReset,
  templatesVariablesFailed,
  templatesVariablesReceived,
  templatesVariablesRequested,
  templatesVariablesReset,
  templatesVariableSearchReceived,
  templatesVariableSearchRequested,
  templatesVariableSearchReset,
  templatesTenantSettingsFailed,
  templatesTenantSettingsReceived,
  templatesTenantSettingsRequested,
  templatesDisclosuresFailed,
  templatesDisclosuresReceived,
  templatesDisclosuresRequested,
  templatesTenantsFailed,
  templatesTenantsReceived,
  templatesTenantsRequested,
} from './templatesMetaSlice';
import { templatesErrorCleared, templatesErrorSet } from './templatesQuerySlice';
import { normalizeListPayload } from './lib/normalizeListPayload';

export const loadTemplateCampaigns = createAsyncThunk(
  'templates/loadCampaigns',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(templatesCampaignsRequested());
    dispatch(templatesErrorCleared());
    try {
      const payload = await fetchCampaigns();
      dispatch(templatesCampaignsReceived(payload));
      return payload;
    } catch (error) {
      dispatch(templatesCampaignsFailed());
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const loadTemplateTags = createAsyncThunk(
  'templates/loadTags',
  async ({ search } = {}, { dispatch, rejectWithValue }) => {
    dispatch(templatesTagsRequested());
    dispatch(templatesErrorCleared());
    try {
      const payload = await fetchTags({ search });
      dispatch(templatesTagsReceived(payload));
      return payload;
    } catch (error) {
      dispatch(templatesTagsFailed());
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const loadTemplateTriggers = createAsyncThunk(
  'templates/loadTriggers',
  async ({ channel } = {}, { dispatch, rejectWithValue }) => {
    dispatch(templatesTriggersRequested());
    dispatch(templatesErrorCleared());
    try {
      const payload = await fetchTriggers({ channel });
      dispatch(templatesTriggersReceived(payload));
      return payload;
    } catch (error) {
      dispatch(templatesTriggersFailed());
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const loadTemplateVariables = createAsyncThunk(
  'templates/loadVariables',
  async ({ channel } = {}, { dispatch, rejectWithValue }) => {
    dispatch(templatesVariablesRequested());
    dispatch(templatesErrorCleared());
    try {
      const payload = await fetchVariables({ channel });
      const normalizedPayload = normalizeListPayload(payload);
      dispatch(templatesVariablesReceived(normalizedPayload));
      return normalizedPayload;
    } catch (error) {
      dispatch(templatesVariablesFailed());
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const searchTemplateVariables = createAsyncThunk(
  'templates/searchVariables',
  async ({ channel, search } = {}, { dispatch, requestId, rejectWithValue }) => {
    dispatch(templatesVariableSearchRequested({ requestId }));
    try {
      const payload = await fetchVariables({ channel, search });
      const normalizedPayload = normalizeListPayload(payload);
      dispatch(templatesVariableSearchReceived({ results: normalizedPayload, requestId }));
      return normalizedPayload;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  },
);

export { templatesVariablesReset, templatesVariableSearchReset, templatesTriggersReset };

export const loadDisclosures = createAsyncThunk(
  'templates/loadDisclosures',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(templatesDisclosuresRequested());
    dispatch(templatesErrorCleared());
    try {
      const payload = await fetchDisclosures();
      const normalized = normalizeListPayload(payload);
      dispatch(templatesDisclosuresReceived(normalized));
      return normalized;
    } catch (error) {
      dispatch(templatesDisclosuresFailed());
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const loadTemplateTenants = createAsyncThunk(
  'templates/loadTenants',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(templatesTenantsRequested());
    dispatch(templatesErrorCleared());
    try {
      const payload = await fetchTenants();
      const normalizedPayload = normalizeListPayload(payload);
      dispatch(templatesTenantsReceived(normalizedPayload));
      return normalizedPayload;
    } catch (error) {
      dispatch(templatesTenantsFailed());
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const loadTenantSettings = createAsyncThunk(
  'templates/loadTenantSettings',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(templatesTenantSettingsRequested());
    try {
      const payload = await fetchTenantSettings();
      dispatch(templatesTenantSettingsReceived(payload));
      return payload;
    } catch (error) {
      dispatch(templatesTenantSettingsFailed());
      return rejectWithValue(error?.message);
    }
  },
);
